import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { EjecutivoCementoComunaOption, EjecutivoCementoRegionOption, EjecutivoCementoUpsertCommand } from '../../../../models/ejecutivo-cemento.model';
import { EjecutivoCementoVm } from '../../../../models/ejecutivo-cemento.vm';

type EjecutivoForm = FormGroup<{
  nombre: FormControl<string>;
  correo: FormControl<string>;
  telefono: FormControl<string>;
  regionCode: FormControl<string>;
  comunaIds: FormControl<number[]>;
  activo: FormControl<boolean>;
}>;

type EjecutivoFormField = keyof EjecutivoForm['controls'];

export type EjecutivoCementoServerErrors = Partial<Record<EjecutivoFormField, string>>;

const emailWithTldValidator: ValidatorFn = (control: AbstractControl<string>): ValidationErrors | null => {
  const value = control.value.trim();
  if (!value || Validators.email(control)) return null;
  return /^[^@\s]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/.test(value)
    ? null
    : { emailTld: true };
};

const chileMobilePhoneValidator: ValidatorFn = (control: AbstractControl<string>): ValidationErrors | null => {
  const value = control.value.trim();
  if (!value) return null;
  if (!/^[0-9\s()+-]+$/.test(value)) return { telefono: true };
  const digits = value.replace(/\D/g, '');
  const localDigits = digits.startsWith('56') ? digits.slice(2) : digits;
  return localDigits.length === 9 && localDigits.startsWith('9') ? null : { telefono: true };
};

@Component({
  selector: 'app-ejecutivo-cemento-detalle-modal',
  templateUrl: './ejecutivo-cemento-detalle-modal.component.html',
  styleUrls: ['./ejecutivo-cemento-detalle-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EjecutivoCementoDetalleModalComponent implements OnChanges, OnDestroy {
  @Input() item: EjecutivoCementoVm | null = null;
  @Input() regions: EjecutivoCementoRegionOption[] = [];
  @Input() geographicItems: EjecutivoCementoComunaOption[] = [];
  @Input() loading = false;
  @Input() serverErrors: EjecutivoCementoServerErrors = {};
  @Input() serverMessage = '';
  @Output() closeRequested = new EventEmitter<void>();
  @Output() saveRequested = new EventEmitter<EjecutivoCementoUpsertCommand>();

  readonly form: EjecutivoForm;
  communeOptions: EjecutivoCementoComunaOption[] = [];
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly regionChangeSubscription: Subscription;
  private readonly formValueChangeSubscription: Subscription;

  constructor(private readonly fb: FormBuilder) {
    this.form = this.fb.group({
      nombre: this.fb.nonNullable.control('', [Validators.required]),
      correo: this.fb.nonNullable.control('', [Validators.required, Validators.email, emailWithTldValidator]),
      telefono: this.fb.nonNullable.control('', [chileMobilePhoneValidator]),
      regionCode: this.fb.nonNullable.control('', [Validators.required]),
      comunaIds: this.fb.nonNullable.control<number[]>([]),
      activo: this.fb.nonNullable.control(true),
    });
    this.regionChangeSubscription = this.form.controls.regionCode.valueChanges.subscribe((regionCode) => {
      this.form.controls.comunaIds.setValue([]);
      this.refreshCommuneOptions(regionCode);
    });
    this.formValueChangeSubscription = this.form.valueChanges.subscribe(() => {
      if (Object.keys(this.serverErrors).length || this.serverMessage) {
        this.serverErrors = {};
        this.serverMessage = '';
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.regionChangeSubscription.unsubscribe();
    this.formValueChangeSubscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['geographicItems']) {
      this.refreshCommuneOptions(this.form.controls.regionCode.value);
    }
    if (changes['item']) this.applyItem();
  }

  get isEditMode(): boolean { return (this.item?.id ?? 0) > 0; }
  get modalTitle(): string { return this.isEditMode ? 'Editar ejecutivo cemento' : 'Crear ejecutivo cemento'; }
  onPhoneInput(value: string): void {
    this.form.controls.telefono.setValue(value);
  }

  onPhoneBlur(): void {
    this.form.controls.telefono.markAsTouched();
  }

  fieldError(name: EjecutivoFormField): string {
    const control = this.form.controls[name];
    if (this.serverErrors[name]) return this.serverErrors[name];
    if (!control.touched || !control.invalid) return '';
    if (control.errors?.['required']) return 'Campo requerido';
    if (control.errors?.['email'] || control.errors?.['emailTld']) {
      return 'Ingrese un correo válido con dominio completo, por ejemplo nombre@empresa.cl.';
    }
    if (control.errors?.['telefono']) {
      return 'Ingrese un celular válido. Ej: 9 1234 5678.';
    }
    return 'Revise el valor ingresado.';
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saveRequested.emit({
      nombre: this.form.controls.nombre.value.trim(),
      correo: this.form.controls.correo.value.trim(),
      telefono: this.form.controls.telefono.value.trim(),
      regionCode: this.form.controls.regionCode.value,
      comunaIds: this.form.controls.comunaIds.value.map(Number),
      activo: this.form.controls.activo.value,
    });
  }

  onClose(): void { this.closeRequested.emit(); }

  private applyItem(): void {
    if (!this.item) {
      this.form.reset(
        { nombre: '', correo: '', telefono: '', regionCode: '', comunaIds: [], activo: true },
        { emitEvent: false },
      );
      this.refreshCommuneOptions('');
      return;
    }
    this.form.reset(
      {
        nombre: this.item.nombre,
        correo: this.item.correo,
        telefono: this.item.telefono,
        regionCode: this.item.regionCode,
        comunaIds: this.item.comunaIds,
        activo: this.item.activo,
      },
      { emitEvent: false },
    );
    this.refreshCommuneOptions(this.item.regionCode);
  }

  private refreshCommuneOptions(regionCode: string): void {
    this.communeOptions = this.geographicItems.filter((item) => item.regionCode === regionCode);
    this.cdr.markForCheck();
  }
}
